/**
 * The PUBLIC site's chrome — the bare platform domain only.
 *
 * These pages had neither. Someone who followed a shared link to an artist
 * page landed on a bare column of names with nothing saying what this site
 * was, no way back to the directory, and no sign-in control — and `Directory`
 * drew its own title as the literal string `MYRAVE`, uppercased, which is not
 * the product's name.
 *
 * A festival subdomain does NOT use this: `FestivalApp` carries the festival's
 * own logo and its bottom tab bar, and MyRave.io appears there only in the
 * footer. The brand in the header belongs to whoever the visitor thinks they
 * are visiting.
 */
import { BrandFlightProvider, HeaderBrand } from './BrandFlight';
import type { ReactNode } from 'react';
import { AppHeader, AppFooter } from '@/ui/chrome';
import { BrandLockup } from '@/ui';
import { VersionMatrixButton } from './VersionMatrix';
import { appUrl, loginUrl, legalUrl, platformUrl, supportUrl } from '@/lib/tenant';
import { MaintenanceBanner } from './platform';
import { LanguageSwitch } from './LanguageSwitch';
import { useT } from '@/i18n';
import { useLocale } from './LocaleProvider';
import { FollowUs, SocialIcons } from './SocialIcons';
import { PeopleCount } from './PeopleCount';

export interface SiteLink { label: string; href: string; icon?: ReactNode }

function FooterCol({ title, items }: { title: string; items: SiteLink[] }) {
  return (
    <div className="app-col">
      <div className="app-col-title">
        {title}
      </div>
      <nav className="app-col-links">
        {items.map(({ label, href }) => (
          <a key={label} href={href} className="app-col-link">
            {label}
          </a>
        ))}
      </nav>
    </div>
  );
}

/**
 * The design's pill-shaped centre navigation.
 *
 * **Only pages that exist.** It was Features, Showcase, Raves, Download and
 * About, every one a link to `/` — five controls that went nowhere, naming
 * pages nobody had written ("real or gone"). An item comes back when its page
 * does.
 */
function HeaderNav() {
  const tr = useT();
  // Upcoming raves — open to everybody; Find a rave (`/raves`) asks for an account (2026-10-27).
  const items: SiteLink[] = [{ label: tr('Raves'), href: appUrl('/') }];
  return (
    <nav className="platform-nav app-topnav">
      {items.map(({ label, href }) => (
        <a key={label} href={href} className="app-topnav-link">
          {label}
        </a>
      ))}
      {/* MyRave's accounts in the header too (the 0.14.76 walk, w-so-footer). */}
      <SocialIcons className="app-header-social" />
    </nav>
  );
}

/**
 * The welcome pages' own navigation (the public site, 2026-10-14): links to
 * the page's sections, as the Stitch design draws them — plain words with an
 * icon, no pill. On a phone it folds into one button (`.app-pubnav-menu`).
 */
function SectionNav({ items }: { items: SiteLink[] }) {
  const tr = useT();
  if (items.length === 0) return null;
  const links = items.map(({ label, href, icon }) => (
    <a key={href} href={href} className="app-pubnav-link">{icon}{label}</a>
  ));
  return (
    <>
      {/* MyRave's accounts beside the links on a computer, "Follow us" in the phone's menu (the 0.14.76 walk,
          w-so-footer: "missing also in the header buttons, or the menu on mobile"). */}
      <nav className="app-pubnav" aria-label={tr('On this page')}>{links}<SocialIcons className="app-header-social" /></nav>
      <details className="app-pubnav-menu">
        <summary className="app-pubnav-toggle" aria-label={tr('On this page')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        </summary>
        <nav className="app-pubnav-sheet" aria-label={tr('On this page')}
          onClick={(e) => { (e.currentTarget.parentElement as HTMLDetailsElement).open = false; }}>
          {links}
          <FollowUs className="app-pubnav-follow" />
        </nav>
      </details>
    </>
  );
}

/**
 * The welcome pages' thin footer (the design's one line): the lockup and a
 * line, then the links that exist — privacy, terms, contact us — and the
 * version matrix.
 */
function ThinFooter() {
  const tr = useT();
  const { locale } = useLocale();
  return (
    <footer className="app-thinfoot" data-print="hide">
      <div className="app-thinfoot-row">
        <div className="app-thinfoot-brand">
          <BrandLockup size={20} href={`${platformUrl()}/`} />
          <span className="app-thinfoot-dot" aria-hidden="true">•</span>
          <span>{tr('Your rave in your pocket — signal or not.')}</span>
        </div>
        <nav className="app-thinfoot-links" aria-label={tr('Company & legal')}>
          <a href={legalUrl('privacy', locale)}>{tr('Privacy policy')}</a>
          <a href={legalUrl('terms', locale)}>{tr('Terms of use')}</a>
          {/* "Contact us" on the welcome pages; inside the app the same page is "Support". */}
          <a href={supportUrl()}>{tr('Contact us')}</a>
          <VersionMatrixButton />
          <PeopleCount />
        </nav>
        {/* The welcome page's footer had none (the 0.14.76 walk, w-so-footer). */}
        <SocialIcons className="app-thinfoot-social" />
      </div>
      <LanguageSwitch />
    </footer>
  );
}

/**
 * The public site's footer.
 *
 * **Every link goes somewhere real.** It had three columns of fourteen links,
 * every one `href="/"` — "Privacy policy" included, on the page app review
 * reads for exactly that link. What is left is what exists: the homepage, the
 * directory, and the privacy policy the Worker serves at `/privacy` on the
 * bare platform domain (`frontend/worker/privacy.ts`) — absolute, because on
 * any other host `/privacy` is the app — the terms of use beside it
 * (`worker/terms.ts`), and Support (`support.…`, open signed out).
 * Cookies, a press kit and the rest come back when they are written.
 */
function SiteFooter() {
  const tr = useT();
  const { locale } = useLocale();
  return (
    <AppFooter
      tagline={tr('The offline-first rave time engine. Timetables, clash warnings and the festival map with no connectivity needed.')}
      links={
        <>
          <FooterCol
            title={tr('Product')}
            items={[
              { label: tr('Home'), href: `${platformUrl()}/` },
              { label: tr('Explore lineups'), href: appUrl('/') },
              { label: tr("What's new"), href: `${platformUrl()}/whats-new` },
            ]}
          />
          <FooterCol
            title={tr('Company & legal')}
            items={[
              { label: tr('Privacy policy'), href: legalUrl('privacy', locale) },
              { label: tr('Terms of use'), href: legalUrl('terms', locale) },
              // Support is the app's page, open signed out (round 5).
              { label: tr('Support'), href: supportUrl() },
            ]}
          />
          <SocialIcons className="platform-chrome-social" />
        </>
      }
      locales={<LanguageSwitch />}
      copyright={<><VersionMatrixButton /><PeopleCount /></>}
    />
  );
}

/**
 * The PUBLIC site's chrome — the bare platform domain.
 *
 * **No account control here, by design.** A person icon on a marketing page
 * asks somebody who has never been here to recognise an affordance; "Log in"
 * and "Register" say what they do. The icon belongs on `app.`, where everyone
 * looking at it has an account.
 *
 * Nobody signed in ever reaches this chrome — `PublicSite` has already sent
 * them to `app.` — so there is no signed-in state to render.
 */
export function PublicChrome({ children, nav, actions, brand, thin }: {
  children: ReactNode;
  /** The welcome pages' section links; other public pages keep the Raves pill. */
  nav?: SiteLink[];
  /** In place of "Log in / Register" — the welcome pages' Sign in + Get the app. */
  actions?: ReactNode;
  /** In place of the MyRave lockup — a rave's landing page brings its own. */
  brand?: ReactNode;
  /** The design's one-line footer. */
  thin?: boolean;
}) {
  const tr = useT();

  return (
    <BrandFlightProvider>
    <div className="app-column">
      <AppHeader
        /*
          * Not the default lockup: the homepage's own, which starts big and
          * centred in the page and flies up into this spot as you scroll. On
          * every other public page it is an ordinary lockup and nothing
          * moves — `HeaderBrand` decides, because only it knows whether a
          * page registered a hero.
          */
        brand={brand ?? <HeaderBrand />}
        controls={nav ? <SectionNav items={nav} /> : <HeaderNav />}
        account={actions ?? (
          /*
            * ONE button, and it GOES somewhere.
            *
            * This was "Log in" and "Register" side by side, both opening the
            * same sheet over the page behind them. Two controls for one
            * destination is a choice nobody wanted to make, and at 320px they
            * were what pushed the header off the screen. `login.myrave.io` is
            * a page a person can bookmark and a password manager recognises.
            */
          <a
            className="header-cta app-cta"
            href={loginUrl()}
          >
            {tr('Log in / Register')}
          </a>
        )}
      />

      <MaintenanceBanner />
      <main className="app-fill">{children}</main>
      {thin ? <ThinFooter /> : <SiteFooter />}

    </div>
    </BrandFlightProvider>
  );
}

